import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getGuiaCategoria, getMaterialCategoria, type GuiaPublica, type MaterialPublico } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { Navbar } from "../components/Navbar";
import { Reveal } from "../components/Reveal";
import { GuiaPreviewModal } from "../components/GuiaPreviewModal";
import { getCategory } from "../lib/categories";
import { parseUtcDate } from "../lib/time";

const CAT_META: Record<string, { titulo: string; subtitulo: string }> = {
  ANSIEDAD:               { titulo: "Respira. Esto que sientes tiene salida.", subtitulo: "Una guía psicoeducativa para entender la ansiedad y herramientas prácticas para sostener los días difíciles. Descárgala gratis y empieza hoy." },
  DEPRESIÓN:              { titulo: "Para los días en que todo se siente gris.", subtitulo: "Recursos de apoyo para atravesar los momentos más difíciles con compasión." },
  AUTOESTIMA:             { titulo: "Para cuando no te sientes suficiente.", subtitulo: "Ejercicios para reconectar con tu valor y empezar a tratarte mejor." },
  RELACIONES:             { titulo: "Para sanar lo que duele en tus vínculos.", subtitulo: "Guías para construir relaciones más sanas y comunicarte con más claridad." },
  ESTRÉS:                 { titulo: "Para cuando ya no puedes más.", subtitulo: "Estrategias para reducir la carga, recuperar energía y poner límites." },
  DUELO:                  { titulo: "Para aprender a soltar lo que amabas.", subtitulo: "Acompañamiento en el proceso de pérdida, sin prisa y sin juicios." },
  TRAUMA:                 { titulo: "Para sanar desde adentro.", subtitulo: "Recursos para procesar experiencias difíciles y recuperar tu seguridad." },
  MINDFULNESS:            { titulo: "Para volver a estar presente.", subtitulo: "Prácticas de atención plena para desconectar del ruido y volver a ti." },
  LÍMITES:                { titulo: "Para aprender a decir no sin culpa.", subtitulo: "Guías para establecer límites sanos en tus relaciones y en el trabajo." },
  "CRECIMIENTO PERSONAL": { titulo: "Para convertirte en quien quieres ser.", subtitulo: "Herramientas para el desarrollo personal desde una perspectiva psicológica." },
};

const TIPO_LABEL: Record<string, string> = {
  plantilla:             "Plantilla",
  ejercicio:              "Ejercicio",
  "material didáctico":  "Material didáctico",
};

function isNew(created_at: string): boolean {
  return Date.now() - parseUtcDate(created_at).getTime() < 7 * 24 * 60 * 60 * 1000;
}

export default function Guia() {
  const { categoria = "" }       = useParams<{ categoria: string }>();
  const navigate                 = useNavigate();
  const { isAuthenticated }      = useAuth();

  const cat    = categoria.toUpperCase().replace(/-/g, " ");
  const meta   = CAT_META[cat] ?? CAT_META["ANSIEDAD"];
  const catCol = getCategory(cat);
  const catSlug = categoria.toLowerCase();

  const [guia,     setGuia]     = useState<GuiaPublica | null>(null);
  const [material, setMaterial] = useState<MaterialPublico[]>([]);
  const [loadingG, setLoadingG] = useState(true);
  const [loadingM, setLoadingM] = useState(true);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    setLoadingG(true);
    getGuiaCategoria(cat)
      .then((r) => setGuia(r.ok ? r.data : null))
      .catch(() => setGuia(null))
      .finally(() => setLoadingG(false));

    setLoadingM(true);
    getMaterialCategoria(cat)
      .then((r) => setMaterial(r.ok ? r.data.material : []))
      .catch(() => setMaterial([]))
      .finally(() => setLoadingM(false));
  }, [cat]);

  function handleDescargar() {
    if (!isAuthenticated) {
      navigate(`/registro?categoria=${catSlug}&utm=dm`);
      return;
    }
    if (guia) window.open(guia.url_r2, "_blank", "noopener");
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--ink)", fontFamily: "var(--font-body)" }}>
      <Navbar variant="minimal" alwaysSolid />

      {/* breadcrumb */}
      <div className="container" style={{ paddingTop: 92, fontSize: 13, color: "var(--muted-3)" }}>
        Guías <span style={{ margin: "0 6px" }}>›</span> <span style={{ color: "var(--copper)", fontWeight: 600 }}>{catCol.label}</span>
      </div>

      {/* hero */}
      <div className="container" style={{ paddingTop: 26, paddingBottom: 10 }}>
        <div className="grid-2" style={{ gridTemplateColumns: "1.1fr .9fr" }}>
          <Reveal>
            <div>
              <span className="badge" style={{ background: catCol.bg, color: catCol.text, fontSize: 13, padding: "8px 16px", marginBottom: 22 }}>
                <span className="badge__dot" style={{ background: catCol.dot }} />Categoría · {catCol.label}
              </span>
              <h1 className="fluid-heading" style={{ fontFamily: "var(--font-display)", fontWeight: 800, lineHeight: 1.04, letterSpacing: "-0.03em", margin: "18px 0 18px" }}>
                {meta.titulo}
              </h1>
              <p style={{ fontSize: 18, lineHeight: 1.6, color: "var(--muted)", margin: "0 0 28px", maxWidth: 480 }}>
                {meta.subtitulo}
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 14, color: "var(--muted-2)", fontSize: 14, flexWrap: "wrap" }}>
                <span>📄 PDF · {guia ? "guía digital" : "próximamente"}</span>
                <span>🧩 Material de apoyo</span>
                <span>⏱ Lectura breve</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={1}>
            <div style={{ position: "relative" }}>
              <div style={{ position: "absolute", right: 14, top: 18, width: "100%", height: "100%", background: "var(--sand)", borderRadius: 22, transform: "rotate(3deg)" }} />
              <div style={{ position: "relative", background: "linear-gradient(160deg,#FBF6EF,#F2E7D9)", border: "1px solid var(--border)", borderRadius: 22, padding: 30, boxShadow: "0 30px 64px -30px rgba(70,55,40,.55)" }}>
                <div style={{
                  aspectRatio: "3/4", borderRadius: 14,
                  background: `repeating-linear-gradient(135deg, ${catCol.bg}, ${catCol.bg} 10px, #E8DBC9 10px, #E8DBC9 20px)`,
                  border: "1px solid #E0CFBB", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  gap: 14, padding: 24, textAlign: "center",
                }}>
                  <img src="/logo-psicodosis.png" style={{ width: 54, height: 54, objectFit: "contain" }} />
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, color: "var(--ink-2)" }}>Guía de {catCol.label}</div>
                  <div style={{ fontSize: 13, color: "var(--muted-3)" }}>PzicoDosis · Psicoeducación</div>
                </div>
                {loadingG ? (
                  <div className="skeleton" style={{ height: 52, marginTop: 20 }} />
                ) : guia ? (
                  <button onClick={() => setShowPreview(true)} className="btn btn-primary btn--full" style={{ marginTop: 20 }}>
                    Descargar gratis ↓
                  </button>
                ) : (
                  <div style={{ marginTop: 20, textAlign: "center", fontSize: 13.5, color: "var(--muted-2)" }}>
                    Estamos preparando esta guía. ¡Vuelve pronto!
                  </div>
                )}
                {guia && (
                  <div style={{ textAlign: "center", fontSize: 13, color: "var(--muted-2)", marginTop: 10 }}>
                    {isAuthenticated ? "Disponible para tu cuenta" : "Regístrate gratis para descargar"}
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* what's inside */}
      <div className="container" style={{ paddingTop: 64 }}>
        <Reveal><h2 className="section-title-display" style={{ fontSize: 30, marginBottom: 26 }}>Qué encontrarás dentro</h2></Reveal>
        <div className="grid-3">
          {[
            { n: "01", title: `Qué es la ${catCol.label.toLowerCase()}`, text: "Entender qué pasa en tu cuerpo y tu mente, sin tecnicismos." },
            { n: "02", title: "Técnicas para el momento", text: "Ejercicios prácticos para sostener los momentos difíciles." },
            { n: "03", title: "Hábitos que ayudan", text: "Pequeños cambios sostenibles para el día a día." },
          ].map((it, i) => (
            <Reveal key={it.n} delay={(i % 4) as 0 | 1 | 2 | 3}>
              <div className="card" style={{ padding: 26, height: "100%" }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, color: catCol.dot, fontSize: 14, marginBottom: 12 }}>{it.n}</div>
                <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 6 }}>{it.title}</div>
                <div style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.5 }}>{it.text}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* support material */}
      <div className="container" style={{ paddingTop: 56 }}>
        <Reveal>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 22, flexWrap: "wrap", gap: 8 }}>
            <h2 className="section-title-display" style={{ fontSize: 30 }}>Material de apoyo</h2>
            {!isAuthenticated && <span style={{ fontSize: 14, color: "var(--copper)", fontWeight: 700 }}>🔒 Regístrate para desbloquear</span>}
          </div>
        </Reveal>

        {loadingM ? (
          <div className="grid-4">
            {[1, 2, 3, 4].map((i) => <div key={i} className="skeleton" style={{ height: 130 }} />)}
          </div>
        ) : material.length === 0 ? (
          <div style={{ background: "rgba(212,196,181,.12)", borderRadius: 12, padding: 32, textAlign: "center", color: "var(--muted)", fontSize: 14.5 }}>
            Próximamente más material para {catCol.label}.
          </div>
        ) : !isAuthenticated ? (
          <div style={{ position: "relative" }}>
            <div className="grid-4" style={{ filter: "blur(3px)", opacity: 0.7, pointerEvents: "none" }}>
              {material.slice(0, 4).map((m) => (
                <div key={m.id} className="card" style={{ padding: 22 }}>
                  <div style={{ height: 80, borderRadius: 10, background: catCol.bg, marginBottom: 14 }} />
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{m.titulo}</div>
                  <div style={{ fontSize: 13, color: "var(--muted-2)" }}>{TIPO_LABEL[m.tipo] ?? m.tipo}</div>
                </div>
              ))}
            </div>
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ background: "rgba(255,253,250,.7)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,.7)", borderRadius: 22, padding: "30px 38px", textAlign: "center", boxShadow: "var(--shadow-card-lg)", maxWidth: 420 }}>
                <div style={{ fontSize: 30, marginBottom: 10 }}>🔓</div>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 21, marginBottom: 8 }}>Desbloquea todo el material</div>
                <div style={{ fontSize: 15, color: "var(--muted)", lineHeight: 1.5, marginBottom: 20 }}>
                  Crea tu cuenta gratis y accede a plantillas, audios y ejercicios de esta y todas las categorías.
                </div>
                <Link to={`/registro?categoria=${catSlug}&utm=dm`} className="btn btn-primary">Crear cuenta gratis</Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid-4">
            {material.map((m, i) => (
              <Reveal key={m.id} delay={(i % 4) as 0 | 1 | 2 | 3}>
                <div className="card card--hover" style={{ padding: 20, position: "relative", height: "100%" }}>
                  {isNew(m.created_at) && <span className="badge-new" style={{ position: "absolute", top: 14, right: 14 }}>NUEVO</span>}
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--copper)", marginBottom: 10 }}>
                    {TIPO_LABEL[m.tipo] ?? m.tipo}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{m.titulo}</div>
                  {m.descripcion && <p style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.5, marginBottom: 14 }}>{m.descripcion}</p>}
                  <a href={m.url_r2} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn--sm btn--full">Descargar</a>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      {/* community teaser */}
      <div className="container" style={{ paddingTop: 62 }}>
        <Reveal>
          <div style={{
            background: "linear-gradient(150deg,#33302B,#413A31)", borderRadius: 28, padding: "42px 44px",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 30,
            position: "relative", overflow: "hidden", flexWrap: "wrap",
          }}>
            <div style={{ position: "absolute", right: -40, top: -40, width: 200, height: 200, background: "radial-gradient(circle,var(--sage),transparent 70%)", opacity: 0.22 }} />
            <div style={{ position: "relative" }}>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 26, color: "var(--on-dark)", marginBottom: 8 }}>
                No estás solo con esto
              </div>
              <div style={{ fontSize: 16, color: "var(--on-dark-muted)", maxWidth: 520, lineHeight: 1.55 }}>
                Únete a la conversación de la comunidad de {catCol.label}: temas activos y personas acompañándose.
              </div>
            </div>
            <Link to="/comunidad" className="btn btn-dark" style={{ position: "relative", flexShrink: 0 }}>Ver comunidad</Link>
          </div>
        </Reveal>
      </div>

      {/* footer */}
      <div className="container" style={{ margin: "62px auto 0", paddingTop: 30, paddingBottom: 48, borderTop: "1px solid var(--border-3)", display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--muted-2)", fontSize: 14, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src="/logo-psicodosis.png" style={{ width: 26, height: 26, objectFit: "contain" }} />
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, color: "var(--ink-2)" }}>PzicoDosis</span>
        </div>
        <span>Privacidad · Términos · Contacto · © {new Date().getFullYear()}</span>
      </div>

      {showPreview && guia && (
        <GuiaPreviewModal guia={guia} onClose={() => setShowPreview(false)} onDownload={() => { setShowPreview(false); handleDescargar(); }} />
      )}
    </div>
  );
}
